import React from 'react'
import { Link } from 'react-router-dom'
import Card from '../shared/Card'
import { useNotification } from '../../context/NotificationContext'
import PaginationFooter from '../shared/PaginationFooter'
import { usePagination } from '../../hooks/usePagination'
import { PAGINATION } from '../../utils/dashboardConstants'

/**
 * Portfolio Updates component for displaying latest company updates
 * 
 * @param {Array} latestUpdates - Array of update objects
 * @param {boolean} loading - Loading state
 * @param {number} pageSize - Optional page size from parent; defaults from dashboard constants
 */
const PortfolioUpdates = ({ latestUpdates, loading, pageSize }) => {
  const { showError } = useNotification()
  
  // Determine page size: prefer prop from parent, fallback to dashboard defaults
  const itemsPerPage = pageSize ?? PAGINATION.MOBILE_TIMELINE_PAGE_SIZE

  // Reuse shared pagination hook to slice updates per page
  const { currentPage, totalPages, currentPageData, setCurrentPage } = usePagination(
    latestUpdates,
    itemsPerPage,
  )

  return (
    <section>
      <Card 
        shadow="medium"
        title="Portfolio Updates"
        subtitle="Investor updates received from your portfolio companies"
      >
        <div className="space-y-4">
          {/* Render loading state or data */}
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading portfolio updates...</div>
          ) : latestUpdates.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No portfolio updates available</div>
          ) : (
            // Render only the current page of updates for performance and UX
            currentPageData.map((update, index) => {
              // Check if we have the required IDs for navigation
              const hasValidIds = update.id && update.startupId
              
              // Common content for both link and div
              const updateContent = (
                <>
                  <h3 className="font-semibold text-gray-900 text-sm mb-1">
                    {update.companyName}
                  </h3>
                  <p className="text-sm text-gray-600 mb-2">
                    {update.description}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">{update.timeAgo}</span>
                    <span className="text-blue-600 text-xs font-medium">
                      Read more →
                    </span>
                  </div>
                </>
              )

              return hasValidIds ? (
                <Link
                  key={update.id || index}
                  to={`/portfolio/${update.startupId}/updates/${update.id}`}
                  className={`pl-4 ${update.color} border-l-4 cursor-pointer hover:bg-gray-50 transition-colors duration-200 rounded-r-md p-2 -m-2 block`}
                >
                  {updateContent}
                </Link>
              ) : (
                <div
                  key={update.id || index}
                  className={`pl-4 ${update.color} border-l-4 cursor-pointer hover:bg-gray-50 transition-colors duration-200 rounded-r-md p-2 -m-2`}
                  onClick={() => {
                    // Show error notification if update ID is missing
                    showError('Unable to show update. Please try again later.', 'Update Error')
                  }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    // Handle keyboard navigation (Enter and Space keys)
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      showError('Unable to show update. Please try again later.', 'Update Error')
                    }
                  }}
                >
                  {updateContent}
                </div>
              )
            })
          )}
        </div>
        
        {/* Pagination footer appears when there are more items than the current page size */}
        {latestUpdates.length > itemsPerPage && (
          <PaginationFooter
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        )}
      </Card>
    </section>
  )
}

export default PortfolioUpdates
